import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Coins, Package, Gift, Clock, Zap, Users, Award, FileText, Key, Calendar, Timer, ArrowUpRight, ArrowDownRight, Star } from 'lucide-react';

export const AVAILABLE_ICONS = [
  { name: 'Package', icon: Package },
  { name: 'Gift', icon: Gift },
  { name: 'Star', icon: Star },
  { name: 'Zap', icon: Zap },
  { name: 'Award', icon: Award },
  { name: 'Coins', icon: Coins },
  { name: 'Key', icon: Key },
  { name: 'FileText', icon: FileText },
  { name: 'Users', icon: Users },
  { name: 'Calendar', icon: Calendar },
  { name: 'Clock', icon: Clock }
];

// Couleurs prédéfinies
export const COLORS = [
  { name: 'Bleu', value: '#3B82F6' },
  { name: 'Or', value: '#F59E0B' },
  { name: 'Vert', value: '#10B981' },
  { name: 'Rouge', value: '#EF4444' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Rose', value: '#EC4899' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Slate', value: '#64748B' }
];

// Composant Countdown Widget
export const CountdownWidget = ({ expirationDate, credits }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!expirationDate) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const expDate = new Date(expirationDate);
      const difference = expDate - now;

      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [expirationDate]);

  if (!expirationDate || credits <= 0) return null;

  const isUrgent = timeLeft.days < 7;

  return (
    <div className={`rounded-lg p-3 ${isExpired ? 'bg-red-100' : isUrgent ? 'bg-orange-100' : 'bg-blue-100'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Timer className={`w-4 h-4 ${isExpired ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-blue-600'}`} />
        <span className={`text-sm font-medium ${isExpired ? 'text-red-800' : isUrgent ? 'text-orange-800' : 'text-blue-800'}`}>
          {isExpired ? 'Crédits expirés' : 'Expiration des crédits temporaires'}
        </span>
      </div>
      {!isExpired && (
        <div className="grid grid-cols-4 gap-2 text-center">
          {[
            { value: timeLeft.days, label: 'J' },
            { value: timeLeft.hours, label: 'H' },
            { value: timeLeft.minutes, label: 'M' },
            { value: timeLeft.seconds, label: 'S' }
          ].map((item, idx) => (
            <div key={idx} className={`rounded p-1 ${isUrgent ? 'bg-orange-200' : 'bg-blue-200'}`}>
              <div className={`text-lg font-bold ${isUrgent ? 'text-orange-800' : 'text-blue-800'}`}>{item.value}</div>
              <div className={`text-xs ${isUrgent ? 'text-orange-600' : 'text-blue-600'}`}>{item.label}</div>
            </div>
          ))}
        </div>
      )}
      <div className={`mt-2 text-center text-sm font-semibold ${isExpired ? 'text-red-700' : isUrgent ? 'text-orange-700' : 'text-blue-700'}`}>
        {credits} crédit(s) {isExpired ? 'perdus' : 'à utiliser'}
      </div>
    </div>
  );
};

// Composant Stats Card
export const StatsCard = ({ icon: Icon, title, value, subtitle, color, trend, trendValue }) => (
  <Card className={`border-l-4`} style={{ borderLeftColor: color }}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end">
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          {trend && (
            <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);
